import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  MapPin,
  FilePdf,
  FileDoc,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const LOGO =
  "https://customer-assets.emergentagent.com/job_d4fda698-50f5-4ffb-9202-1cc296025403/artifacts/9jmcuyor_UrbanConnect_RGB_Logo.png";

const STATUS_STYLE = {
  pending: "bg-warning/15 text-warning",
  approved: "bg-success/15 text-success",
  rejected: "bg-danger/15 text-danger",
};

function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function setStatus(u, action) {
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(`${u.name || u.email} ${action === "approve" ? "approved" : "revoked"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  }

  if (loading) return <p className="text-steel">Loading team…</p>;

  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");

  const Row = ({ u }) => (
    <div
      data-testid={`user-row-${u.id}`}
      className="bg-white border-2 border-line rounded-2xl p-4 flex items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[u.status] || ""}`}>
            {u.status}
          </span>
          {u.role === "admin" && (
            <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-navy/10 text-navy inline-flex items-center gap-1">
              <ShieldCheck size={13} weight="bold" /> admin
            </span>
          )}
        </div>
        <p className="font-head font-semibold text-navydark truncate">{u.name || "—"}</p>
        <p className="text-sm text-steel truncate">
          {u.email} &nbsp;•&nbsp; {u.inspection_count} inspection{u.inspection_count === 1 ? "" : "s"}
        </p>
      </div>
      {u.role !== "admin" && u.id !== user?.id && (
        <div className="flex gap-2 shrink-0">
          {u.status !== "approved" && (
            <button
              data-testid={`approve-${u.id}`}
              onClick={() => setStatus(u, "approve")}
              className="h-11 px-3 rounded-full bg-success text-white font-head font-semibold text-sm inline-flex items-center gap-1"
            >
              <CheckCircle size={18} weight="bold" /> Approve
            </button>
          )}
          {u.status !== "rejected" && (
            <button
              data-testid={`reject-${u.id}`}
              onClick={() => setStatus(u, "reject")}
              className="h-11 px-3 rounded-full border-2 border-danger text-danger font-head font-semibold text-sm inline-flex items-center gap-1"
            >
              <XCircle size={18} weight="bold" /> {u.status === "approved" ? "Revoke" : "Reject"}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <div>
          <h3 className="font-head font-bold text-navydark mb-3 flex items-center gap-2">
            Awaiting approval
            <span className="text-xs bg-warning text-white px-2 py-0.5 rounded-full">{pending.length}</span>
          </h3>
          <div className="space-y-3">
            {pending.map((u) => (
              <Row key={u.id} u={u} />
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="font-head font-bold text-navydark mb-3">All team members</h3>
        <div className="space-y-3">
          {others.map((u) => (
            <Row key={u.id} u={u} />
          ))}
        </div>
      </div>
    </div>
  );
}

function InspectionsTab() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/inspections");
        setItems(data.inspections);
      } catch (e) {
        toast.error("Could not load inspections");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function exportReport(clientId, title, format) {
    setExportingId(clientId + format);
    try {
      const res = await api.get(`/admin/inspections/${clientId}/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (title || "crash-inspection").replace(/[^a-z0-9]+/gi, "_");
      a.download = `${safe}.${format === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExportingId(null);
    }
  }

  if (loading) return <p className="text-steel">Loading inspections…</p>;
  if (items.length === 0) return <p className="text-steel">No inspections have been synced yet.</p>;

  return (
    <div className="space-y-3">
      {items.map((insp) => {
        const ct = insp.data?.crash_type;
        const loc = insp.data?.road_name || insp.data?.state_highway;
        return (
          <div
            key={insp.client_id}
            data-testid={`admin-inspection-${insp.client_id}`}
            className="bg-white border-2 border-line rounded-2xl p-4"
          >
            <div
              className="cursor-pointer"
              onClick={() => nav(`/admin/inspection/${insp.client_id}`)}
            >
              <div className="flex items-center gap-2 mb-1">
                {ct && (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-navy/10 text-navy">
                    {ct}
                  </span>
                )}
                <span className="text-xs font-semibold text-steel">{insp.owner?.name || insp.owner?.email}</span>
              </div>
              <p className="font-head font-semibold text-navydark truncate">
                {insp.title || "Untitled inspection"}
              </p>
              <p className="text-sm text-steel truncate flex items-center gap-1">
                {loc && (
                  <>
                    <MapPin size={14} weight="bold" /> {loc} &nbsp;•&nbsp;
                  </>
                )}
                {new Date(insp.updated_at).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                data-testid={`admin-export-pdf-${insp.client_id}`}
                disabled={exportingId === insp.client_id + "pdf"}
                onClick={() => exportReport(insp.client_id, insp.title, "pdf")}
                className="h-11 px-3 rounded-full bg-navy text-white font-head font-semibold text-sm inline-flex items-center gap-1"
              >
                <FilePdf size={18} weight="bold" /> PDF
              </button>
              <button
                data-testid={`admin-export-docx-${insp.client_id}`}
                disabled={exportingId === insp.client_id + "docx"}
                onClick={() => exportReport(insp.client_id, insp.title, "docx")}
                className="h-11 px-3 rounded-full bg-teal text-white font-head font-semibold text-sm inline-flex items-center gap-1"
              >
                <FileDoc size={18} weight="bold" /> Word
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("users");

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-20 bg-white border-b-2 border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="admin-back-btn"
            onClick={() => nav("/")}
            className="w-11 h-11 rounded-full border-2 border-line flex items-center justify-center text-navy"
            aria-label="Back"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <img src={LOGO} alt="Urban Connection" className="h-8" />
          <span className="font-head font-bold text-navydark">Admin</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 p-1 bg-white rounded-full border-2 border-line w-full max-w-sm">
          {[
            ["users", "Team"],
            ["inspections", "All inspections"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`flex-1 h-11 rounded-full font-head font-semibold transition-colors ${
                tab === k ? "bg-navy text-white" : "text-steel"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "users" ? <UsersTab /> : <InspectionsTab />}
      </main>
    </div>
  );
}
